import { flex } from '@fabric-ui/theme';
import { TargetGroups } from '@shared/Tickets/utils';
import { EntityProjectionName } from '@utils/entityProjectionNames';
import { getEscapedValue } from '@utils/utils';
import { Outegration, OutegrationConfig, TARGET_MATCHING_CATEGORY_ENUM, TARGET_MATCHING_TYPE_ENUM } from '@views/Outegrations/types';
import { getAssetScoreField, getFindingScoreField, getTicketScoreField } from '@views/Tickets/utils';
import { DwQueryRequest } from '../CustomDashboards/types';
import { fixTypeOptionsMap, pivotOptionsMap } from './FixesTab/pivotFixTable.utils';

export const getCommentsCollectSql = (proj: string, alias: string | null = 'comments') =>
  `COLLECT_IF(json_object(comment: json_object(key: ${proj}.comments._key, userId: ${proj}.comments.user_id, created: ${proj}.comments.created, updated: ${proj}.comments.last_update, text: ${proj}.comments.text, userName: ${proj}.comments.username, userEmail: ${proj}.comments.user_email)), ${proj}.comments._key != null) as ${alias}`;

const getCommentAttachmentsCollectSql = (proj: string) =>
  `COLLECT_IF(json_object(commentAttachment: json_object(key: ${proj}.comments.attachments._key, original_name: ${proj}.comments.attachments.original_name, stored_name: ${proj}.comments.attachments.stored_name, content_type: ${proj}.comments.attachments.content_type, related_entity_key: ${proj}.comments.attachments.related_entity_key)), ${proj}.comments.attachments._key != null) as commentAttachments`;

const getExceptionsCollectSql = (key: string, isAdvanceExceptionEnabled: boolean) =>
  isAdvanceExceptionEnabled
    ? `COLLECT_IF(json_object(exception: json_object(key: ${key}._key, requested_sla: ${key}.requested_sla, requester_email: ${key}.requester_email, requester_name: ${key}.requester.name, reviewer_email: ${key}.reviewer_email, reviewer_name: ${key}.reviewer.name, status: ${key}.current_status, scope: ${key}.scope, type: ${key}.type)), ${key}._key != null) as exceptions,`
    : '';

export type TicketDetailSqlParams = {
  ticketKey: string;
  outegrationsQuery: string;
  titleField?: string;
  enableExceptions?: boolean;
  isNewRiskScoreEnabled?: boolean;
  isAdvanceExceptionEnabled?: boolean;
  includeAgenticSummary?: boolean;
  isGlobalThreats?: boolean;
};

export const getTicketSqlV2 = ({
  ticketKey,
  outegrationsQuery,
  titleField,
  enableExceptions,
  isNewRiskScoreEnabled = false,
}: TicketDetailSqlParams) => `SELECT
            ticket._key,
            ticket._operation as operation,
            ticket.severity as severity, 
            ticket.created as created, 
            ${titleField} as title, 
            ticket.sla AS sla,
            ticket.first_seen as firstSeen,
            ticket.finding_count as allFindingCount,
            ticket.active_finding_count as findingCount,
            ticket.asset_count as allAssetCount,
            ticket.active_asset_count as assetCount, 
            ticket.integration_info as integration,
            ${getTicketScoreField(isNewRiskScoreEnabled)} as severityScore,
            ticket.current_status.name as avalorStatus, 
            ticket.current_status.timestamp as statusTimestamp, 
            ticket.original_severity_score as originalSeverityScore, 
            ticket.original_severity as originalSeverity,
            ticket.is_fixable as isFixable,
            ticket.ai_fix as ai_fix,
            ticket.grouping_explanation as groupingExplanation,
            ticket.locked,
            ticket.state,
            ${
              enableExceptions
                ? 'COLLECT_IF(json_object(exception: json_object(key: ticket.exception._key, requested_sla: ticket.exception.requested_sla, requester_email: ticket.exception.requester_email, requester_name: ticket.exception.requester.name, reviewer_email: ticket.exception.reviewer_email, reviewer_name: ticket.exception.reviewer.name, status: ticket.exception.current_status, scope: ticket.exception.scope, type: ticket.exception.type)), ticket.exception._key != null) as exceptions,' +
                  'IF(COUNT_DISTINCT_IF(exception._key, exception_status_bucket.closed != true) > 0, true, false) AS hasOpenExceptions,'
                : ''
            }
            ${getCommentsCollectSql('ticket')},
            ${getCommentAttachmentsCollectSql('ticket')},
            COLLECT_IF(json_object(attachment: json_object(key: ticket.attachments._key, original_name: ticket.attachments.original_name, stored_name: ticket.attachments.stored_name, content_type: ticket.attachments.content_type, related_entity_key: ticket.attachments.related_entity_key)), ticket.attachments._key != null) as attachments,
            remediation_percentage_granular as remediation,
            ticket.type as ticketType ${outegrationsQuery ? `,${outegrationsQuery}` : ''}
          FROM tickets WHERE ticket._key='${getEscapedValue(ticketKey)}'`;

export const getIncidentSqlV2 = ({ ticketKey, outegrationsQuery, titleField }: TicketDetailSqlParams) => `SELECT
            incident._key,
            incident.severity as severity,
            incident.created as created,
            incident.type as ticketType,
            ${titleField} as title,
            incident.sla AS sla,
            incident.first_seen as firstSeen,
            active_alerts_granular as allAlertCount,
            COUNT_DISTINCT(event._key) as allEventCount,
            incident.integration_info as integration,
            incident.severity_score as severityScore,
            incident.current_status.name as avalorStatus,
            incident.grouping_explanation as groupingExplanation,
            ${getCommentsCollectSql('incident')},
            ${getCommentAttachmentsCollectSql('incident')},
            COLLECT_IF(json_object(attachment: json_object(key: incident.attachments._key, original_name: incident.attachments.original_name, stored_name: incident.attachments.stored_name, content_type: incident.attachments.content_type, related_entity_key: incident.attachments.related_entity_key)), incident.attachments._key != null) as attachments,
            incident.original_severity_score as originalSeverityScore,
            incident.original_severity as originalSeverity
            ,attack_path._key as attackPathKey
            ,incident_risk_mass_granular as riskMassGranular
            ${outegrationsQuery ? `,${outegrationsQuery}` : ''}
            FROM incidents
            WHERE incident._key='${getEscapedValue(ticketKey)}'
            GROUP BY incident._key, severity, created, ticketType, title, sla,
            firstSeen, allAlertCount, integration, severityScore, avalorStatus,
            originalSeverityScore, originalSeverity, groupingExplanation`;

export const getAssetsSqlV2 = ({
  ticketKey,
  outegrationsQuery,
  titleField,
  isNewRiskScoreEnabled = false,
}: TicketDetailSqlParams) => `SELECT
            asset._key,
            ${titleField} as title,
            asset.first_seen as firstSeen,
            asset.created as created,
            ${getAssetScoreField(isNewRiskScoreEnabled)} as severityScore,
            asset.type as ticketType,
            ${getCommentsCollectSql('asset')},
            ${getCommentAttachmentsCollectSql('asset')},
            total_policy_violations_granular as totalPolicyViolations
            ${outegrationsQuery ? `,${outegrationsQuery}` : ''}
          FROM uber_assets
          WHERE asset._key='${getEscapedValue(ticketKey)}'
          GROUP BY asset._key, title, firstSeen, created, ticketType`;

export const getFindingSqlV2 = ({
  ticketKey,
  outegrationsQuery,
  titleField,
  isNewRiskScoreEnabled = false,
  isAdvanceExceptionEnabled = false,
}: TicketDetailSqlParams) => `SELECT
            finding._key,
            ${titleField} as title,
            finding.first_seen as firstSeen,
            finding.created as created,
            finding.type as ticketType,
            finding.original_severity_score as originalSeverityScore,
            ${getFindingScoreField(isNewRiskScoreEnabled)} as severityScore,
            finding.severity as severity,
            finding.state as state,
            finding.integration_info as integration,
            ${getCommentsCollectSql('finding')},
            ${getExceptionsCollectSql('finding_rule_based_exception', isAdvanceExceptionEnabled)}
            ${getCommentAttachmentsCollectSql('finding')}
            ${outegrationsQuery ? `,${outegrationsQuery}` : ''}
          FROM uber_findings
          WHERE finding._key='${getEscapedValue(ticketKey)}'`;

export const getTenantsSql = ({ ticketKey, titleField }: TicketDetailSqlParams) => `SELECT
            tenant._key,
            ${titleField} as title,
            tenant.first_seen as firstSeen,
            tenant.severity_score as severityScore,
            tenant.severity as severity
          FROM uber_tenants
          WHERE tenant._key='${getEscapedValue(ticketKey)}'`;

export const getIdentitiesSql = ({ ticketKey, titleField }: TicketDetailSqlParams) => `SELECT
            user._key,
            ${titleField} as title,
            user.first_seen                                                         as firstSeen
          FROM uber_users
          WHERE user._key='${getEscapedValue(ticketKey)}'`;

export const getIdentityFindingsSql = ({ ticketKey }: TicketDetailSqlParams) => `SELECT
            identity_finding._key,
            identity_finding.title as title,
            identity_finding.first_seen as firstSeen,
            identity_finding.severity_score as severityScore,
            identity_finding.severity as severity,
            user._key as userAvalorKey,
            tenant._key as tenantAvalorKey,
            COALESCE(identity_finding.description, vulnerability.description) as description,
            vulnerability.impact as impact,
            uber_identity_findings.source_key as sourceKey,
            vulnerability.references as references,
            vulnerability.remediation_flowchart as chartDefinition
          FROM uber_identity_findings
          WHERE identity_finding._key='${getEscapedValue(ticketKey)}'`;

export const getPolicyViolationSql = ({ ticketKey, outegrationsQuery, titleField }: TicketDetailSqlParams) => `SELECT
            policy_violation._key,
            ${titleField} as title,
            policy_violation.first_seen as firstSeen,
            policy_violation.severity_score as severityScore,
            policy_violation.severity as severity,
            policy_violation.created as created,
            policy_violation.integration_info as integration,
            policy_violation.state as state
           ${outegrationsQuery ? `,${outegrationsQuery}` : ''}
          FROM uber_policy_violations
          WHERE policy_violation._key='${getEscapedValue(ticketKey)}'`;

export const getViolationTicketsSql = ({
  ticketKey,
  outegrationsQuery,
  titleField,
  includeAgenticSummary = false,
}: TicketDetailSqlParams) => `SELECT
            violation_ticket._key,
            ${titleField} as title,
            violation_ticket.first_seen as firstSeen,
            violation_ticket.severity_score as severityScore,
            violation_ticket.severity as severity,
            violation_ticket.created as created,
            violation_ticket.state as state,
            violation_ticket.current_status as avalorStatus,
            violation_ticket.integration_info as integration,
            total_policy_violations_granular as totalPolicyViolations,
            total_assets_active_and_inactive as allAssetCount,
            ${getCommentsCollectSql('violation_ticket')},
            ${getCommentAttachmentsCollectSql('violation_ticket')},
            COLLECT_IF(json_object(attachment: json_object(key: violation_ticket.attachments._key, original_name: violation_ticket.attachments.original_name, stored_name: violation_ticket.attachments.stored_name, content_type: violation_ticket.attachments.content_type, related_entity_key: violation_ticket.attachments.related_entity_key)), violation_ticket.attachments._key != null) as attachments
            ${includeAgenticSummary ? `,violation_ticket.agentic_summary.summary as agenticSummary` : ''}
            ${outegrationsQuery ? `,${outegrationsQuery}` : ''}
          FROM violation_tickets
          WHERE violation_ticket._key='${getEscapedValue(ticketKey)}'`;

export const getAlertsSql = ({ ticketKey, outegrationsQuery, titleField, isGlobalThreats = false }: TicketDetailSqlParams) => `SELECT
            alert._key,
            ${titleField} as title,
            alert.current_status as avalorStatus,
            COUNT_DISTINCT(event._key) as allEventCount,
            alert.summary_attributes as summaryAttributes,
            alert.description as description,
            alert.first_seen as firstSeen,
            alert.last_seen as lastSeen,
            alert.created as created,
            alert.type as ticketType,
            alert.rule_id as ruleId,
            alert.rule_name as ruleName,
            alert.severity_score as severityScore,
            alert.severity as severity,
            alert.original_severity_score as originalSeverityScore,
            alert.original_severity as originalSeverity,
            alert.tactic as tacticId,
            alert.technique as techniqueId,
            alert.source_key as sourceKey,
            alert.verdict as verdict,
            alert.source_names as sourceNames,
            total_incidents_granular as allIncidentsCount,
            ${
              isGlobalThreats
                ? `COLLECT_DISTINCT_JSON((
                    'type', threat.type,
                    'name', threat.name,
                    'confidence', threat.confidence_level,
                    'findings', threat.findings,
                    'verdict', threat.verdict,
                    'supporting_evidence', threat.supporting_evidence,
                    'contradicting_evidence', threat.contradicting_evidence,
                    'analysis_of_discrepancies', threat.analysis_of_discrepancies
                  ), alert._key)`
                : `COLLECT_DISTINCT_JSON((
                    'type', ioc.type,
                    'name', ioc.name,
                    'confidence', ioc.confidence_level,
                    'findings', ioc.verdict,
                    'verdict', CASE WHEN ioc.is_malicious = true THEN 'Malicious' ELSE 'Benign' END,
                    'supporting_evidence', ioc.supporting_evidence,
                    'contradicting_evidence', ioc.contradicting_evidence,
                    'analysis_of_discrepancies', ioc.analysis_of_discrepancies
                  ), alert._key)`
            } as evidence
            ${outegrationsQuery ? `,${outegrationsQuery}` : ''}
          FROM uber_alerts
          WHERE alert._key='${getEscapedValue(ticketKey)}'
          GROUP BY alert._key, title, summaryAttributes, description, firstSeen, lastSeen, created,
          ticketType, severityScore, severity, originalSeverityScore, originalSeverity,
          ruleId, ruleName, tacticId, techniqueId, sourceNames`;

export const getExceptionsSql = ({ ticketKey, outegrationsQuery }: TicketDetailSqlParams) => `SELECT
            exception._key,
            exception.created as created,
            exception.current_status as avalorStatus,
            exception.integration_info as integration,
            ticket.sla as originalTicketSLA,
            exception.related_entity_key as relatedEntityKey,
            exception.related_proj_name,
            exception.type,
            exception.scope,
            exception.approved_sla,
            exception.requested_sla,
            exception.reason,
            exception.first_seen as firstSeen,
            requester.name,
            exception.requester_email,
            exception.original_related_entity_sla,
            ('for: ' + ticket.title) as title,
            COLLECT_IF(json_object(attachment: json_object(key: exception.attachments._key, original_name: exception.attachments.original_name, stored_name: exception.attachments.stored_name, content_type: exception.attachments.content_type, related_entity_key: exception.attachments.related_entity_key)), exception.attachments._key != null) as attachments,
            ${getCommentsCollectSql('exception')},
            ${getCommentAttachmentsCollectSql('exception')}
            ${outegrationsQuery ? `,${outegrationsQuery}` : ''}
          FROM uber_exceptions
          WHERE exception._key='${getEscapedValue(ticketKey)}'`;

export const getExceptionsIdnpSql = ({ ticketKey, outegrationsQuery }: TicketDetailSqlParams) => `SELECT
            exception._key,
            exception.created as created,
            exception.current_status as avalorStatus,
            exception.integration_info as integration,
            '' as originalTicketSLA,
            exception.related_entity_key as relatedEntityKey,
            exception.related_proj_name,
            exception.type,
            exception.scope,    
            exception.approved_sla,
            exception.requested_sla,
            exception.reason,
            exception.first_seen as firstSeen,
            ('for: ' + identity_findings.title) as title,
            identity_findings.severity_score as severity_score,
            identity_findings.severity as severity,
            COLLECT_IF(json_object(attachment: json_object(key: exception.attachments._key, original_name: exception.attachments.original_name, stored_name: exception.attachments.stored_name, content_type: exception.attachments.content_type, related_entity_key: exception.attachments.related_entity_key)), exception.attachments._key != null) as attachments,
            ${getCommentsCollectSql('exception')},
            ${getCommentAttachmentsCollectSql('exception')}
            ${outegrationsQuery ? `,${outegrationsQuery}` : ''}
          FROM uber_exceptions
          WHERE exception._key='${getEscapedValue(ticketKey)}'`;

export const beforeEditStyle = {
  content: '""',
  position: 'absolute',
  top: '-2px',
  left: '-12px',
  padding: '2px 12px',
  width: '100%',
  height: '100%',
  boxSizing: 'content-box',
  borderRadius: '8px',
  zIndex: -1,
  backgroundColor: 'transparent',
  transition: theme =>
    theme.transitions.create('background-color', {
      duration: theme.transitions.duration.shorter,
    }),
};
export const editFieldStyle = {
  width: '100%',
  cursor: 'pointer',
  outline: 'none',
  position: 'relative',
  ...flex.itemsCenter,
  gap: 2,
  ':before': {
    ...beforeEditStyle,
  },
  '&:hover:before, &:focus:before': {
    backgroundColor: 'var(--color-surface-list-hover)',
  },
  '.MuiOutlinedInput-root, .MuiOutlinedInput-root:hover': { backgroundColor: 'transparent' },
  '&:focus > div': {
    textOverflow: 'unset',
  },
};

export const defaultFixesTabConfig = {
  fixType: fixTypeOptionsMap.kb,
  filters: {},
  fixesView: pivotOptionsMap.component,
  selectedHeadersIndexMap: Object.keys(pivotOptionsMap).reduce((acc, key) => ({ ...acc, [key]: 0 }), {}),
};

export const shouldShowForProjection = (config: { projNameToShow?: string[] }, projectionName: string) =>
  !config?.projNameToShow || config.projNameToShow.includes(projectionName);

export type ConfigField = {
  displayName: string;
  field: string;
  fieldValue?: any;
  group?: string;
  fieldType?: string;
  isManualOverrideEnabled?: boolean;
  showNull?: string;
  isLoading?: boolean;
};

export const excludeFromUIConfigPerProjection = {
  uber_exceptions: {
    aliases: [],
    fields: ['ticket.sla', 'requester.name', 'exception.requester_email', 'exception.original_related_entity_sla', 'exception.rules'],
  },
  uber_assets: {
    aliases: ['parent_asset', 'child_asset', 'chain_uber_assets'],
    fields: [
      'cloud_organization_level',
      'cloud_unit_level',
      'last_state_transition',
      'tags',
      'asset.is_decoy',
      'operating_system._key',
      'asset.certificate_expiry',
      'asset.certificate_issuer_common_name',
      'asset.certificate_issuer_country',
      'asset.certificate_issuer_organization',
      'asset.certificate_issuer_organization_unit',
      'asset.certificate_subject_common_name',
      'asset.certificate_subject_country',
      'asset.certificate_subject_key_id',
      'asset.certificate_subject_organization',
      'asset.certificate_valid_since',
      'location.first_seen',
      'operating_system.first_seen',
      'location.state',
      'operating_system.state',
      'location.created',
      'operating_system.created',
      'owner.created',
      'location.last_update',
      'operating_system.last_update',
      'owner.last_update',
      'operating_system.source_key',
      'asset.os_key',
      'owner.source_key',
      'location.last_seen',
      'operating_system.last_seen',
      'location.source_names',
      'operating_system.source_names',
      'owner.source_names',
      'location.locked',
      'operating_system.locked',
      'asset.longitude',
      'location.longitude',
      'asset.latitude',
      'location.latitude',
      'asset.location_id',
      'location.location_id',
      'asset.normalized_type',
      'asset.normalized_os',
      'asset.normalized_os_build',
      'asset.normalized_os_version',
      'asset.discovery_chain_asset_ids',
      'asset.profile_ids',
    ],
  },
};

export const specificFieldsFromUIConfigPerProjection = {
  uber_alerts: {
    fields: [
      'asset.name',
      'asset.domain',
      'alert.destination_url',
      'alert.referrer_url',
      'alert.file_name',
      'alert_actor.cmdline',
      'artifact.hash_md5',
      'artifact.hash_sha256',
      'artifact.request_id',
      'artifact.request_method',
      'artifact.user_agent',
      'artifact.request_data_size',
      'user.user_name',
    ],
  },
};

export const getMetricsByProjName = (
  projName: EntityProjectionName,
  shouldShowIncidentsTabsCount = false
): DwQueryRequest['select']['metrics'] =>
  ({
    uber_assets: [
      { name: 'total_findings_granular', alias: 'total_findings_granular' },
      { name: 'total_tickets_granular', alias: 'total_tickets_granular' },
      ...(shouldShowIncidentsTabsCount
        ? [
            { name: 'total_alerts_granular', alias: 'allAlertCount' },
            { name: 'total_incidents_granular', alias: 'allIncidentsCount' },
          ]
        : []),
    ],
    uber_tenants: [
      { name: 'total_identity_findings', alias: 'total_identity_findings' },
      { name: 'total_users_granular', alias: 'total_users_granular' },
    ],
    uber_users: [
      { name: 'total_identity_findings', alias: 'total_identity_findings' },
      { name: 'total_alerts_granular', alias: 'allAlertCount' },
      { name: 'active_groups', alias: 'total_groups' },
      { name: 'active_roles', alias: 'total_roles' },
      { name: 'active_app_role_assignments', alias: 'total_app_role_assignments' },
      { name: 'active_applications', alias: 'total_applications' },
      { name: 'total_active_tenants', alias: 'total_active_tenants' },
      { name: 'active_user_assets', alias: 'total_user_assets' },
    ],
    uber_identity_findings: [
      { name: 'affected_assets_identity_findings', alias: 'affected_assets_identity_findings' },
      { name: 'affected_groups_identity_findings', alias: 'affected_groups_identity_findings' },
      { name: 'affected_identities_identity_findings', alias: 'affected_identities_identity_findings' },
      { name: 'affected_tenants_identity_findings', alias: 'affected_tenants_identity_findings' },
    ],
    uber_findings: [
      {
        name: 'finding.finding_evidence.evidence',
        alias: 'findingEvidence',
      },
      {
        name: 'finding.finding_evidence_output.evidence',
        alias: 'findingEvidenceOutput',
      },
    ],
  })[projName];

const AS = ' as ';
export const getFieldId = field => {
  const index = field.toLowerCase().indexOf(AS);
  if (index === -1) {
    return field;
  }
  const fieldAs = field.slice(index + AS.length);
  return fieldAs.includes('"') ? fieldAs.slice(1, -1) : fieldAs;
};

export const getOptionGroupNumber = (
  { id, targetMatchingType, integrationType }: { id: string; targetMatchingType: any; integrationType: string },
  action: string,
  ticket: any,
  destinationsMap: Record<string, OutegrationConfig>
) =>
  (targetMatchingType === TARGET_MATCHING_TYPE_ENUM.ALWAYS || ticket[`target_${id}`]) &&
  destinationsMap[integrationType]?.supports?.[action] &&
  targetMatchingType !== TARGET_MATCHING_TYPE_ENUM.NEVER
    ? TargetGroups.MATCHING_TICKET
    : TargetGroups.NOT_MATCHING_TICKET;

export const getHasScannerOutegrations = (outegrations: Outegration[]) =>
  (outegrations?.filter(a => a.category === TARGET_MATCHING_CATEGORY_ENUM.SCANNER).length ?? 0) > 0;
